import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsSlider from "components/settingsConfig/uiComponents/SettingsSlider";
import { postListDefaultSettings, PostListSettingsStateType } from "./PostListUtils";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AutoWidgetQueryBehavior, WidgetRequest } from "system/widget/Widget";

const requestSize = 250;

const defaultWidgetRequest = {
  name: 'post-list',
  endpoint: 'query/records',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

const recordsRequestHighlightConfig = {highlight: { pre: `<i>`, post: "</i>" }};

export function BuildDefaultPostListRequests(): WidgetRequest[] | undefined  {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    records: {
      ...recordsRequestHighlightConfig,
      match_info: true,
      size: requestSize
    }
  };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export default function PostListSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [settingsState, setSettingsState] = useState<PostListSettingsStateType> ({ ...postListDefaultSettings, ...props.widget?.data?.settings});
  const settingsOnChange = (updatedState: PostListSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const sortingFieldOptions:OptionsValueType[] = useMemo(() => {
    if (!props.queryInfo?.fields) return [];

    const allowedTypes = ["integer", "decimal", "timestamp"];
    const queryFields = props.queryInfo.fields
      // Filter down to only fields that are of allowed types and support querying (Excludes Records/Social Universe)
      .filter(field => allowedTypes.includes(field.type) && field.query_supported !== false)
      .map(field => ({
        label: field.label,
        value: field.id,
      }));

    return [
      { label: i18n.t("none"), value: "" },
      ...queryFields
    ];
  }, [props.queryInfo]);

  const sortingDirectionOptions: OptionsValueType[] = [
    { label: i18n.t("descending"), value: "desc" },
    { label: i18n.t("ascending"), value: "asc" }
  ];

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = {
      records: {
        ...recordsRequestHighlightConfig,
        match_info: true,
        size: requestSize
      }
    };
    if (settingsState.sortField !== "") {
      requestInitBody.records.sort = [{field: settingsState.sortField, direction: settingsState.sortDirection}];
    }

    widgetRequest.requestInit = { body: requestInitBody };
    props.saveWidgetDataAndRequests({
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      maxWidth="sm"
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("display")]}
      title={"Post List Widget Settings"}
    >
      <>
        {tabValue === 0 &&
          <SplitSettingsWrapper>
            <SettingsOptions settingsState={settingsState} onChange={settingsOnChange}>
              <SettingsAccordion label={"Pagination"}>
                <SettingsSlider
                  keyid="pageSize"
                  label={"Page Size"}
                  min={1}
                  max={50}
                  step={1}
                  sliderValue={settingsState.pageSize}
                  defaultValue={25}
                  showCurrentValue={true}
                />
              </SettingsAccordion>
              <SettingsAccordion label={"Sorting"}>
                {/* Sort Field */}
                <SettingsSingleSelect
                  keyid="sortField"
                  label={"Field"}
                  options={sortingFieldOptions}
                  value={sortingFieldOptions.find((option: OptionsValueType) => option.value === settingsState.sortField)}
                  defaultValue={sortingFieldOptions.find((option: OptionsValueType) => (option.value === "")) ?? sortingFieldOptions[0]}
                />

                {/* Sort Direction */}
                {settingsState.sortField !== "" && (
                  <SettingsSingleSelect
                    keyid="sortDirection"
                    label={"Direction"}
                    options={sortingDirectionOptions}
                    value={sortingDirectionOptions.find((option: OptionsValueType) => option.value === settingsState.sortDirection)}
                    defaultValue={sortingDirectionOptions.find((option: OptionsValueType) => (option.value === "desc")) ?? sortingDirectionOptions[1]}
                  />
                )}
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
      </>
    </TabbedDialog>
  );
}